import type { ContentType, FieldError } from '@apuestapr/contracts';
import { Button, Field, Input, Select, Textarea, buttonVariants, cn, toast } from '@apuestapr/ui';
import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createContentApi } from './api.ts';
import {
  getDashboardContentDefinition,
  type ContentCreateByType,
  type ContentFormField,
  type ContentItemByType,
  type ContentUpdateByType,
} from './catalog.ts';
import { ImageField } from './ImageField.tsx';
import { PublishFields, publishFieldId, type PublishValues } from './PublishFields.tsx';
import { UnsavedChangesGuard } from './UnsavedChangesGuard.tsx';
import {
  contentFieldId,
  errorForField,
  errorsByField,
  normalizedContentValues,
  payloadForContent,
  valuesForContent,
  type ContentFormValues,
} from './useContentForm.ts';

const otherCategory = '__other__';

const failureMessage = (failure: ApiFailure): string => {
  if (failure.kind === 'malformed') return 'La respuesta guardada no coincide con el contrato.';
  if (failure.kind === 'unreachable') return 'No se pudo conectar con la API.';
  if (failure.status === 403) return 'No tienes permiso para guardar este contenido.';
  return failure.message;
};

const errorTarget = (path: string, fields: readonly ContentFormField[]): string => {
  if (path === 'status' || path === 'publishAt' || path === 'unpublishAt') {
    return publishFieldId(path);
  }
  const field = fields.find(({ key }) => path === key || path.startsWith(`${key}.`));
  return contentFieldId(field?.key ?? path);
};

interface CategoryFieldProps {
  readonly field: Extract<ContentFormField, { readonly control: 'category' }>;
  readonly known: readonly string[];
  readonly value: string;
  readonly error?: string | undefined;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}

const CategoryField = ({
  field,
  known,
  value,
  error,
  disabled,
  onChange,
}: CategoryFieldProps): ReactElement => {
  const [custom, setCustom] = useState(value === '');
  const unknownExisting = !custom && value !== '' && !known.includes(value);
  const selection = custom ? otherCategory : value;
  return (
    <div className="grid gap-3">
      <Field
        controlId={contentFieldId(field.key)}
        description="Estas opciones son sugerencias editoriales, no una enumeración de la base de datos."
        error={error}
        label={field.label}
        required
      >
        {(control) => (
          <Select
            {...control}
            disabled={disabled}
            value={selection}
            onChange={(event) => {
              const selected = event.currentTarget.value;
              const useCustom = selected === otherCategory;
              setCustom(useCustom);
              onChange(useCustom ? '' : selected);
            }}
          >
            {known.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
            {unknownExisting ? <option value={value}>{value} (valor existente)</option> : null}
            <option value={otherCategory}>Otra</option>
          </Select>
        )}
      </Field>
      {selection === otherCategory ? (
        <Field
          controlId={`${contentFieldId(field.key)}-other`}
          error={error}
          label="Otra categoría"
          required
        >
          {(control) => (
            <Input
              {...control}
              disabled={disabled}
              value={value}
              onChange={(event) => onChange(event.currentTarget.value)}
            />
          )}
        </Field>
      ) : null}
    </div>
  );
};

interface EditableFieldProps {
  readonly field: ContentFormField;
  readonly knownCategories: readonly string[];
  readonly value: string;
  readonly error?: string | undefined;
  readonly disabled: boolean;
  readonly csrfToken: string;
  readonly uploader: ReturnType<typeof useSessionContext>['imageUploader'];
  readonly create: boolean;
  readonly onChange: (value: string) => void;
}

const EditableField = ({
  field,
  knownCategories,
  value,
  error,
  disabled,
  csrfToken,
  uploader,
  create,
  onChange,
}: EditableFieldProps): ReactElement => {
  if (field.control === 'image') {
    return (
      <ImageField
        controlId={contentFieldId(field.key)}
        csrfToken={csrfToken}
        disabled={disabled}
        error={error}
        label={field.label}
        nullable={field.nullable ?? false}
        uploader={uploader}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (field.control === 'category') {
    return (
      <CategoryField
        disabled={disabled}
        error={error}
        field={field}
        known={knownCategories}
        value={value}
        onChange={onChange}
      />
    );
  }

  const required = !(field.nullable ?? false) && !(create && (field.optionalOnCreate ?? false));
  return (
    <Field
      controlId={contentFieldId(field.key)}
      description={field.description}
      error={error}
      label={field.label}
      required={required}
    >
      {(control) => {
        if (field.control === 'textarea' || field.control === 'keywords') {
          return (
            <Textarea
              {...control}
              disabled={disabled}
              rows={field.key === 'contenido' ? 12 : undefined}
              value={value}
              onChange={(event) => onChange(event.currentTarget.value)}
            />
          );
        }
        if (field.control === 'select') {
          return (
            <Select
              {...control}
              disabled={disabled}
              value={value}
              onChange={(event) => onChange(event.currentTarget.value)}
            >
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          );
        }
        return (
          <Input
            {...control}
            disabled={disabled}
            inputMode={field.control === 'number' ? 'numeric' : undefined}
            type={field.control === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(event) => onChange(event.currentTarget.value)}
          />
        );
      }}
    </Field>
  );
};

export interface ContentFormPageProps<Type extends ContentType> {
  readonly type: Type;
  readonly item: ContentItemByType[Type] | null;
}

/** One controlled, catalog-driven create/edit form for all accepted content types. */
export const ContentFormPage = <Type extends ContentType>({
  type,
  item,
}: ContentFormPageProps<Type>): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const definition = getDashboardContentDefinition(type);
  const api = useMemo(() => createContentApi(session.client), [session.client]);
  const navigate = useNavigate();
  const create = item === null;
  const [savedItem, setSavedItem] = useState<ContentItemByType[Type] | null>(item);
  const [values, setValues] = useState<ContentFormValues>(() => valuesForContent(type, item));
  const [baseline, setBaseline] = useState(() => normalizedContentValues(type, values));
  const [fieldErrors, setFieldErrors] = useState<readonly FieldError[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const dirty = normalizedContentValues(type, values) !== baseline;
  const mappedErrors = errorsByField(fieldErrors);

  useEffect(() => {
    if (pendingNavigation !== null && !dirty) navigate(pendingNavigation);
  }, [dirty, navigate, pendingNavigation]);

  if (authenticated === null) return <></>;

  const updateValue = (key: string, value: string | null): void => {
    setValues((current) => ({ ...current, [key]: value }) as ContentFormValues);
    setFieldErrors((current) => current.filter((error) => error.path !== key));
  };

  const save = async (preview: boolean): Promise<void> => {
    setFailure(null);
    setFieldErrors([]);
    if (values.status === 'scheduled' && values.publishAt === null) {
      setFieldErrors([
        { path: 'publishAt', message: 'A scheduled item requires a publication time' },
      ]);
      queueMicrotask(() => document.getElementById(publishFieldId('publishAt'))?.focus());
      return;
    }
    const payload = payloadForContent(type, values, create);
    const parsed = (create ? definition.createSchema : definition.updateSchema).safeParse(payload);
    if (!parsed.success) {
      const errors = parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      }));
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    const result = create
      ? await api.create(type, parsed.data as ContentCreateByType[Type], authenticated.csrfToken)
      : await api.update(
          type,
          item.id,
          parsed.data as ContentUpdateByType[Type],
          authenticated.csrfToken,
        );
    setSaving(false);
    if (!result.ok) {
      session.handleFailure(result.failure);
      if (
        result.failure.kind === 'http' &&
        (result.failure.status === 400 || result.failure.status === 409)
      ) {
        setFieldErrors(result.failure.details ?? []);
      } else {
        setFailure(result.failure);
      }
      return;
    }

    setSavedItem(result.data);
    const savedValues = valuesForContent(type, result.data);
    setValues(savedValues);
    setBaseline(normalizedContentValues(type, savedValues));
    toast({ title: create ? 'Entrada creada' : 'Cambios guardados' });
    if (preview) setPendingNavigation(`/content/${type}/${String(result.data.id)}/preview`);
    else if (create) setPendingNavigation(`/content/${type}/${String(result.data.id)}/edit`);
  };

  const heading = create ? `Nueva entrada · ${definition.label}` : `Editar · ${definition.label}`;
  return (
    <section aria-labelledby="content-form-heading" className="grid gap-6">
      <UnsavedChangesGuard dirty={dirty} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0" id="content-form-heading">
            {heading}
          </h2>
          <p className="mb-0 text-muted-foreground">
            Todos los campos se envían como fueron escritos; no se aplica un editor enriquecido.
          </p>
        </div>
        {savedItem === null ? null : (
          <Link
            className={cn(buttonVariants({ variant: 'secondary' }))}
            to={`/content/${type}/${String(savedItem.id)}/preview`}
          >
            Vista previa
          </Link>
        )}
      </div>

      {fieldErrors.length === 0 ? null : (
        <div className="rounded-xl border-2 border-foreground bg-card p-4" role="alert">
          <h3 className="mt-0 text-base">Corrige los campos señalados</h3>
          <ul className="mb-0">
            {fieldErrors.map((error, index) => (
              <li key={`${error.path}:${error.message}:${String(index)}`}>
                <a href={`#${errorTarget(error.path, definition.fields)}`}>
                  {error.path || 'Formulario'}: {error.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {failure === null ? null : <p role="alert">{failureMessage(failure)}</p>}

      <form
        className="grid gap-6"
        noValidate
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          void save(false);
        }}
      >
        <div className="grid gap-5">
          {definition.fields.map((field) => (
            <EditableField
              key={field.key}
              create={create}
              csrfToken={authenticated.csrfToken}
              disabled={saving}
              error={errorForField(mappedErrors, field.key)}
              field={field}
              knownCategories={definition.knownCategories}
              uploader={session.imageUploader}
              value={String(values[field.key] ?? '')}
              onChange={(value) => updateValue(field.key, value)}
            />
          ))}
        </div>
        <PublishFields
          disabled={saving}
          effectiveVisibility={savedItem?.effectiveVisibility ?? null}
          errors={mappedErrors}
          values={values as PublishValues}
          onChange={(key, value) => updateValue(key, value)}
        />
        <div className="flex flex-wrap gap-3">
          <Button loading={saving} loadingLabel="Guardando contenido…" type="submit">
            Guardar
          </Button>
          <Button
            loading={saving}
            loadingLabel="Guardando para vista previa…"
            type="button"
            variant="secondary"
            onClick={() => void save(true)}
          >
            Guardar y ver vista previa
          </Button>
          <Link className={buttonVariants({ variant: 'outline' })} to={`/content/${type}`}>
            Volver a la lista
          </Link>
        </div>
      </form>
    </section>
  );
};
